import React, { useEffect, useState } from "react";
import { addPasskey, listPasskeys, passkeyError, passkeysSupported, PasskeyItem, removePasskey } from "./passkeys";

const date = (value: string) => new Date(value).toLocaleDateString("en-JM", { day: "numeric", month: "short", year: "numeric" });

/** "Passkeys" on My profile: add Face ID / Touch ID / fingerprint sign-in, see and remove saved passkeys. */
export default function Passkeys({ token }: { token: string }) {
  const [items, setItems] = useState<PasskeyItem[] | null>(null);
  const [busy, setBusy] = useState(""), [error, setError] = useState(""), [notice, setNotice] = useState("");
  useEffect(() => {
    // Browsers without passkeys don't show this section, so don't ask for the list.
    if (!passkeysSupported()) return;
    let active = true;
    listPasskeys(token).then((list) => active && setItems(list)).catch(() => active && setItems([]));
    return () => { active = false; };
  }, [token]);
  if (!passkeysSupported()) return null;
  const add = async () => {
    setBusy("add"); setError(""); setNotice("");
    try {
      const item = await addPasskey(token);
      setItems((old) => [item, ...(old || [])]);
      setNotice(`Passkey added. Next time, choose “Sign in with a passkey” and use ${item.name === "iPhone" || item.name === "iPad" || item.name === "Mac" ? "Face ID or Touch ID" : "your fingerprint, face or PIN"}.`);
    } catch (err) { setError(passkeyError(err, true)); } finally { setBusy(""); }
  };
  const remove = async (item: PasskeyItem) => {
    if (!window.confirm(`Remove the passkey “${item.name}”? You can still sign in with your password.`)) return;
    setBusy(item.id); setError(""); setNotice("");
    try { await removePasskey(token, item.id); setItems((old) => (old || []).filter((p) => p.id !== item.id)); setNotice(`Passkey “${item.name}” removed.`); }
    catch (err) { setError(passkeyError(err, true)); } finally { setBusy(""); }
  };
  return (
    <section className="account-passkeys" aria-labelledby="passkeys-title">
      <h2 id="passkeys-title">Passkeys</h2>
      <p>Sign in with Face ID, Touch ID, your fingerprint or Windows Hello instead of typing your password. Your fingerprint or face never leaves your device.</p>
      {error && <p role="alert" className="account-error">{error}</p>}
      {notice && <p role="status" className="account-success">{notice}</p>}
      {items === null ? <p role="status">Loading passkeys…</p> : items.length > 0 && (
        <ul>
          {items.map((item) => (
            <li key={item.id}>
              <span><strong>{item.name}</strong><small>Added {date(item.createdAt)}{item.lastUsedAt ? ` · Last used ${date(item.lastUsedAt)}` : ""}{item.synced ? " · Synced to your other devices" : ""}</small></span>
              <button type="button" disabled={!!busy} onClick={() => remove(item)} aria-label={`Remove passkey ${item.name}`}>{busy === item.id ? "Removing…" : "Remove"}</button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" className="account-passkey-add" disabled={!!busy || items === null} onClick={add}>{busy === "add" ? "Waiting for your device…" : "Add a passkey (Face ID / fingerprint)"}</button>
    </section>
  );
}
